import { ref } from 'vue'
import type { Ref } from 'vue'
import type {
  Participant,
  RegistrationDetail,
  ParticipantFilters,
  PaginationMeta,
  UpdateRegistrationPayload,
} from '@/types/participants'
import {
  getEditionParticipants as apiGetEditionParticipants,
  getRaceParticipants as apiGetRaceParticipants,
  getRegistration as apiGetRegistration,
  updateRegistration as apiUpdateRegistration,
  cancelRegistration as apiCancelRegistration,
  promoteRegistration as apiPromoteRegistration,
  bulkPromoteRegistrations as apiBulkPromoteRegistrations,
  resendConfirmationEmail as apiResendConfirmationEmail,
} from '@/api/participants'
import type { CancelRegistrationResult } from '@/api/participants'

const participants = ref<Participant[]>([])
const registration = ref<RegistrationDetail | null>(null)
const paginationMeta = ref<PaginationMeta | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

async function fetchEditionParticipants(
  editionId: number,
  filters: ParticipantFilters = {},
): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const response = await apiGetEditionParticipants(editionId, filters)
    participants.value = response.data
    paginationMeta.value = response.meta
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function fetchRaceParticipants(
  raceId: number,
  filters: ParticipantFilters = {},
): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const response = await apiGetRaceParticipants(raceId, filters)
    participants.value = response.data
    paginationMeta.value = response.meta
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function fetchRegistration(registrationId: number): Promise<void> {
  loading.value = true
  error.value = null
  try {
    registration.value = await apiGetRegistration(registrationId)
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function saveRegistration(
  registrationId: number,
  payload: UpdateRegistrationPayload,
): Promise<RegistrationDetail> {
  const result = await apiUpdateRegistration(registrationId, payload)
  registration.value = result
  return result
}

async function cancelReg(
  registrationId: number,
  refundAmount: number | null,
): Promise<CancelRegistrationResult> {
  const result = await apiCancelRegistration(registrationId, refundAmount)
  registration.value = result.registration
  return result
}

async function promoteReg(registrationId: number): Promise<RegistrationDetail> {
  const result = await apiPromoteRegistration(registrationId)
  registration.value = result
  return result
}

async function bulkPromoteRegs(registrationIds: number[]): Promise<RegistrationDetail[]> {
  return await apiBulkPromoteRegistrations(registrationIds)
}

// Deliberately does not touch the module-level `loading`/`error` singletons:
// those drive the whole-table spinner and the page-level error banner, and a
// per-row action must not blank the table or hijack the page banner. Errors
// propagate to the caller.
async function resendConfirmation(registrationId: number): Promise<void> {
  await apiResendConfirmationEmail(registrationId)
}

export function useParticipants(): {
  participants: Ref<Participant[]>
  registration: Ref<RegistrationDetail | null>
  paginationMeta: Ref<PaginationMeta | null>
  loading: Ref<boolean>
  error: Ref<string | null>
  fetchEditionParticipants: (editionId: number, filters?: ParticipantFilters) => Promise<void>
  fetchRaceParticipants: (raceId: number, filters?: ParticipantFilters) => Promise<void>
  fetchRegistration: (registrationId: number) => Promise<void>
  saveRegistration: (
    registrationId: number,
    payload: UpdateRegistrationPayload,
  ) => Promise<RegistrationDetail>
  cancelRegistration: (
    registrationId: number,
    refundAmount: number | null,
  ) => Promise<CancelRegistrationResult>
  promoteRegistration: (registrationId: number) => Promise<RegistrationDetail>
  bulkPromoteRegistrations: (registrationIds: number[]) => Promise<RegistrationDetail[]>
  resendConfirmationEmail: (registrationId: number) => Promise<void>
} {
  return {
    participants,
    registration,
    paginationMeta,
    loading,
    error,
    fetchEditionParticipants,
    fetchRaceParticipants,
    fetchRegistration,
    saveRegistration,
    cancelRegistration: cancelReg,
    promoteRegistration: promoteReg,
    bulkPromoteRegistrations: bulkPromoteRegs,
    resendConfirmationEmail: resendConfirmation,
  }
}
